<template>
  <div class="border-2 px-3 py-4 radius-20 border-spect-gray overflow-x-auto">
    <DataTable
      :value="loading ? usersSkeleton : users"
      :stripedRows="true"
      :paginator="true"
      :rows="50"
      :rowsPerPageOptions="[10, 20, 50, 100, 150, 200]"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="$t('common.pagination_template')"
      class="p-datatable-sm min-w-[80rem]"
    >
      <!-- Full Name Column -->
      <Column
        field="fullName"
        :header="$t('user_progression.full_name')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <div v-if="loading" class="flex items-center gap-2">
            <Skeleton shape="circle" size="2rem"></Skeleton>
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <div v-else class="flex items-center gap-2">
            <Avatar
              :image="data.avatar"
              :label="getInitials(data.fullName)"
              shape="circle"
              size="normal"
              class="me-2"
            />
            <span class="no-whitespace">{{ data.fullName }}</span>
          </div>
        </template>
      </Column>

      <!-- Service Column -->
      <Column
        field="service"
        :header="$t('user_progression.service')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <Skeleton v-if="loading" width="6rem" height="1rem"></Skeleton>
          <Tag
            v-else
            :value="data.service.name"
            severity="info"
            class="service-tag"
          />
        </template>
      </Column>

      <!-- Status Column -->
      <Column
        field="status"
        :header="$t('user_progression.status')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <Skeleton v-if="loading" width="5rem" height="1rem"></Skeleton>
          <Tag
            v-else
            :value="getUserStatus(data.role)"
            :severity="getStatusSeverity(data.role?.name)"
          />
        </template>
      </Column>

      <!-- Email Column -->
      <!-- <Column field="email" :header="$t('user_progression.email')" sortable>
        <template #body="{ data }">
          <Skeleton v-if="loading" width="10rem" height="1rem"></Skeleton>
          <a v-else :href="`mailto:${data.email}`" class="email-link">
            {{ data.email }}
          </a>
        </template>
      </Column> -->

      <!-- Phone Column -->
      <!-- <Column field="phone" :header="$t('user_progression.phone')" sortable>
        <template #body="{ data }">
          <Skeleton v-if="loading" width="8rem" height="1rem"></Skeleton>
          <span v-else>{{ data.phone || '...' }}</span>
        </template>
      </Column> -->

      <!-- Cyberscore Column -->
      <Column
        field="cyberscore"
        :header="$t('user_progression.cyberscore')"
        sortable
        class="flex justify-center items-center white-space-nowrap"
      >
        <template #body="{ data }">
          <Skeleton v-if="loading" width="4rem" height="1rem"></Skeleton>
          <Tag
            v-else
            :value="data.rawCyberscore"
            :severity="data.rawCyberscore >= 80 ? 'success' : 'danger'"
            class="score-tag"
          />
        </template>
      </Column>

      <!-- Progression Column -->
      <Column
        field="progression"
        :header="$t('user_progression.progression')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <div v-if="loading" class="progress-container">
            <Skeleton width="100%" height="1rem"></Skeleton>
          </div>
          <div v-else class="progress-container">
            <ProgressBar
              :value="data.progressionValue"
              class="progress-bar w-3/4"
            />
            <!-- <span class="progress-text">{{ data.progressionValue }}%</span> -->
          </div>
        </template>
      </Column>

      <!-- Certification Column -->
      <Column
        field="certification"
        :header="$t('user_progression.certification')"
        sortable
        class="flex justify-center items-center white-space-nowrap"
        v-if="!isPluzz"
      >
        <template #body="{ data }">
          <Skeleton v-if="loading" width="4rem" height="1rem"></Skeleton>
          <Tag
            v-else
            :value="data.rawCertification"
            :severity="data.rawCertification >= 80 ? 'success' : 'danger'"
            class="score-tag"
          />
        </template>
      </Column>

      <!-- Registration Date Column -->
      <Column
        field="registrationDate"
        :header="$t('user_progression.registration')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <Skeleton v-if="loading" width="6rem" height="1rem"></Skeleton>
          <span v-else>{{ formatDate(data.registrationDate) }}</span>
        </template>
      </Column>

      <!-- Start Date Column -->
      <Column
        field="startDate"
        :header="$t('user_progression.start_date')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <Skeleton v-if="loading" width="6rem" height="1rem"></Skeleton>
          <Tag
            v-else-if="data.startDate"
            :value="formatDate(data.startDate)"
            severity="info"
          />
          <Tag
            v-else
            :value="$t('user_progression.not_started')"
            severity="secondary"
            class="no-whitespace"
          />
        </template>
      </Column>

      <!-- Completion Date Column -->
      <Column
        field="completionDate"
        :header="$t('user_progression.completion_date')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <Skeleton v-if="loading" width="6rem" height="1rem"></Skeleton>
          <Tag
            v-else-if="data.completionDate"
            :value="formatDate(data.completionDate)"
            severity="success"
          />
          <Tag
            v-else
            :value="$t('user_progression.not_started')"
            severity="secondary"
            class="no-whitespace"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup>
/*  eslint-disable */
import { defineProps, computed } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tag from "primevue/tag";
import Avatar from "primevue/avatar";
import Skeleton from "primevue/skeleton";
import ProgressBar from "primevue/progressbar";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const props = defineProps({
  users: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isPluzz: {
    type: Boolean,
    default: false,
  },
});

// Default image for avatars
const defaultImage = "/images/defaultImage.png";

// Create skeleton data for loading state
const usersSkeleton = computed(() => {
  return Array.from({ length: 10 }, (_, index) => ({
    id: index,
    fullName: "",
    service: "",
    status: "",
    email: "",
    phone: "",
    cyberscore: "",
    progression: "",
    progressionValue: 0,
    certification: "",
    registrationDate: "",
    startDate: "",
    completionDate: "",
  }));
});

// Helper function to get initials from full name
const getInitials = (fullName) => {
  if (!fullName) return "";
  const names = fullName.split(" ");
  return names
    .map((name) => name.charAt(0))
    .join("")
    .toUpperCase();
};

const getUserStatus = (role) => {
  if (role.name == "manager") {
    return t("user_progression.manager");
  }
  return t("user_progression.member");
};
// Helper function to determine status severity
const getStatusSeverity = (statusType) => {
  switch (statusType) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "inactive":
    default:
      return "info";
  }
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch (error) {
    return dateString;
  }
};
</script>

<style lang="scss" scoped>
@use "../../assets/scss/variables.scss" as *;
@use "../../assets/scss/app/common.scss" as *;
.progress-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.progress-bar {
  //flex: 1;
  height: 1rem;
  padding-top: 2px;
  padding-bottom: 2px;
}

.progress-text {
  font-size: 0.75rem;
  font-weight: 500;
  min-width: 3rem;
  text-align: right;
}

.service-tag,
.score-tag {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.email-link {
  color: #3b82f6;
  text-decoration: none;
}

.email-link:hover {
  text-decoration: underline;
}

.d-flex {
  display: flex;
}

.align-items-center {
  align-items: center;
}

.me-2 {
  margin-right: 0.5rem;
  background: var(--p-avatar-background) !important;
}
</style>
